import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Lock, KeyRound, ArrowRight, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Input';
import { Spinner } from '../components/Dialog';
import { setAdminKey } from '../adminApi';

/**
 * SignInPage — operator sign in with admin API key.
 * Single text field (the key is opaque). Validates by hitting /admin/customers.
 */
export default function SignInPage({ onAuthed }) {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    setAdminKey(key);
    try {
      // Validate the key by hitting a protected endpoint.
      const r = await fetch('/api/admin/customers?limit=1', { headers: { 'x-admin-key': key } });
      if (!r.ok) {
        if (r.status === 401) throw new Error('Invalid admin key');
        throw new Error(`Server returned ${r.status}`);
      }
      onAuthed?.(key);
    } catch (e) {
      setError(e.message || 'Failed to sign in');
      setAdminKey(null); // clear
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--brand)/0.06),transparent_50%)]" />
      <div className="flex-1 flex items-center justify-center p-6 relative">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <img src="/logo.png" alt="Datames" className="h-9 w-9 rounded-md object-cover shadow-[0_0_0_1px_hsl(var(--border))]" draggable="false" />
            <div className="leading-tight">
              <div className="text-base font-semibold text-fg">Datames</div>
              <div className="text-2xs text-fg-muted uppercase tracking-wider">Operator console</div>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-fg tracking-tight">Sign in</h1>
            <p className="text-sm text-fg-muted mt-1.5">
              Enter your operator API key to access the admin console.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Admin API key" htmlFor="key">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
                <Input
                  id="key"
                  type="password"
                  required
                  autoFocus
                  autoComplete="off"
                  placeholder="admin_..."
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="pl-9 font-mono"
                />
              </div>
            </Field>

            {error && (
              <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-2xs text-fg-subtle text-center">
              Lost your admin key? Contact the founder — there is no self-service recovery.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}